import React, { useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { updateUserGeneralSettings } from '../../../redux/actions/index'

import {
  Dialog, DialogTitle, DialogContent,
  Grid, Typography, Paper,
  Accordion, AccordionDetails, AccordionSummary,
  IconButton, List, ListItem, ListItemText
} from '@material-ui/core'

import { makeStyles } from '@material-ui/core/styles'

import {
  Close as CloseIcon, ExpandMore as ExpandIcon, Person as PersonIcon, Domain as DomainIcon,
  SupervisedUserCircle as SupervisedUserCircleIcon,
  Style as StyleIcon, AccountTree as AccountTreeIcon, MenuBook as MenuBookIcon,
  Gavel as GavelIcon, Language as LanguageIcon, NotificationsActive as NotificationsActiveIcon,
  SettingsApplications as SettingsApplicationsIcon, Web as WebIcon,
  Power as IntegrationsIcon, BeachAccess as CiscoIcon
} from '@material-ui/icons'

// import AssignmentIcon from '@material-ui/icons/Assignment'
// import DescriptionIcon from '@material-ui/icons/Description'
// import ListAltIcon from '@material-ui/icons/ListAlt'

// user profile
import UserGeneral from '../forms/UserProfile/UserGeneral'
// import UserMyReports from '../forms/UserProfile/UserMyReports'

// organization profile
import OrganizationGeneral from '../forms/OrganizationProfile/OrganizationGeneral'
import OrganizationContacts from '../forms/OrganizationProfile/OrganizationContacts'
import OrganizationLicense from '../forms/OrganizationProfile/OrganizationLicense'
import OrganizationSettings from '../forms/OrganizationProfile/OrganizationSettings'
import OrganizationEmail from '../forms/OrganizationProfile/OrganizationEmail'

// user management
import UserMgmtUsers from '../forms/UserManagement/UserMgmtUsers'

// Supervisor Groups
import SupervisorGroups from '../forms/UserManagement/SupervisorGroups'

// user roles
import UserRolesList from '../forms/UserRoles/UserRolesList'

// organizational management
import OrgUnitMgmt from '../forms/OrganizationalUnitMgmt/OrgUnitMgmt'

// word vault
import WordVault from '../forms/WordVault/WordVault'

// whitelist management
import Whitelist from '../forms/WhitelistMgmt/Whitelist'

// app exclusion
import AppExclusionList from '../forms/AppExclusion/AppExclusionList'

// acceptable use policies
import AUPlist from '../forms/AcceptableUsePolicies/AUPlist'

// alert management
import UserMgmtAlerts from '../forms/Alerts/UserMgmtAlerts'

// weblogs
import WeblogList from '../forms/Weblogs/WeblogList'

// integrations
import { CiscoSplash, CiscoRequests, CiscoLists } from '../forms/Integrations'
// import integrationsList from '...'

import log from 'loglevel'
// import { ElevioIntegration } from '../tools'

import { statusTypes, statusPermissions } from '../../../utils/variables'

const useStyles = makeStyles((theme) => ({
  settingsTitle: {
    color: '#97b557'
  },
  settingsPaper: {
    backgroundColor: 'rgba(0, 0, 0, 0.08)',
    width: '100%',
    marginLeft: 'auto',
    marginRight: 'auto',
    height: 'fit-content'
  },
  accordionHeader: {
    backgroundColor: '#a4d164',
    borderRadius: '0.125em'
  },
  formPaper: {
    backgroundColor: 'rgba(0, 0, 0, 0.08)',
    width: '100%',
    marginLeft: 'auto',
    padding: theme.spacing(2),
    marginRight: 'auto',
    height: 'fit-content'
  },
  settingsContent: {
    minHeight: '50em'
  },
  selectedListItem: {
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    '&:hover': {
      backgroundColor: theme.palette.action.hover
    }
  },
  accordionContent: {
    padding: theme.spacing(0)
    // backgroundColor: theme.palette.text.primary,
    // color: theme.palette.background.paper
  },
  listItem: {
    backgroundColor: theme.palette.text.secondary,
    color: theme.palette.background.paper,
    '&:hover': {
      backgroundColor: theme.palette.text.disabled
    }
  },
  settingsDialog: {
    zIndex: '1050 !important'
  }
}))

/**
 * Creates a modal that displays settings information for the user/org
 */
const SettingsScreen = ({ toggleModal, open }) => {
  const dispatch = useDispatch()
  const classes = useStyles()

  const [navPanel, setNavPanel] = useState('users')
  const [selectedForm, setSelectedForm] = useState('user-general')

  const [canEditAccount, setCanEditAccount] = useState(false)
  const [canEditSupervisors, setCanEditSupervisors] = useState(false)
  const [canEditRoles, setCanEditRoles] = useState(false)
  const [canEditUnits, setCanEditUnits] = useState(false)
  const [canEditWords, setCanEditWords] = useState(false)
  const [canEditAUPs, setCanEditAUPs] = useState(false)
  const [canEditWhitelists, setCanEditWhitelists] = useState(false)
  const [canEditAlerts, setCanEditAlerts] = useState(false)
  const [canEditAppExclusions, setCanEditAppExclusions] = useState(false)
  const [canViewWeblogs, setCanViewWeblogs] = useState(false)
  const [canEditIntegrations, setCanEditIntegrations] = useState(false)
  const [canApproveBloc, setCanApproveBloc] = useState(false)

  const [isCiscoDisabled, setIsCiscoDisabled] = useState(false)

  const permissions = useSelector(state => state.auth.permissions)

  const { ciscoUmbrellaSettings: { summary: ciscoSummary } = {} } = useSelector(state => state.integrations)

  useMemo(() => {
    const canViewUnviewedCaptures = permissions.includes(statusPermissions[statusTypes.UNVIEWED])

    permissions.forEach((permissionKey) => {
      if (permissionKey === 'account-editor') { setCanEditAccount(true) }
      if (permissionKey === 'supervisor-editor') { setCanEditSupervisors(true) }
      if (permissionKey === 'role-editor') { setCanEditRoles(true) }
      if (permissionKey === 'unit-editor') { setCanEditUnits(true) }
      if (permissionKey === 'word-editor') { setCanEditWords(true) }
      if (permissionKey === 'aup-editor') { setCanEditAUPs(true) }
      if (permissionKey === 'whitelist-editor') { setCanEditWhitelists(true) }
      if (permissionKey === 'alert-editor' && canViewUnviewedCaptures) { setCanEditAlerts(true) }
      if (permissionKey === 'exclusions-editor') { setCanEditAppExclusions(true) }
      if (permissionKey === 'weblog-viewer') { setCanViewWeblogs(true) }
      if (permissionKey === 'integration-editor') { setCanEditIntegrations(true) }
      if (permissionKey === 'cisco-approve-block') { setCanApproveBloc(true) }
    })
  }, [permissions])

  useMemo(() => {
    if (ciscoSummary && ciscoSummary.enabled != null) { setIsCiscoDisabled(!ciscoSummary.enabled) }
  }, [ciscoSummary])

  const getSelectedForm = () => {
    switch (selectedForm) {
      // user profile
      case 'user-general':
        return <UserGeneral onSubmit={onSubmit} setSelectedForm={setSelectedForm} />
      // case 'user-my-reports':
      //   return <UserMyReports onSubmit={onSubmit} />
      // organization profile
      case 'organization-general':
        return <OrganizationGeneral onSubmit={onSubmit} />
      case 'organization-contacts':
        return <OrganizationContacts onSubmit={onSubmit} />
      case 'organization-license':
        return <OrganizationLicense onSubmit={onSubmit} />
      case 'organization-settings':
        return <OrganizationSettings onSubmit={onSubmit} />
      case 'organization-email':
        return <OrganizationEmail onSubmit={onSubmit} />
      // user management
      case 'user-mgmt-list-users':
        return <UserMgmtUsers onSubmit={onSubmit} />
      case 'supervisor-groups':
        return <SupervisorGroups onSubmit={onSubmit} />
      // user roles
      case 'user-roles-list':
        return <UserRolesList onSubmit={onSubmit} />
      // organizational management
      case 'org-mgmt':
        return <OrgUnitMgmt onSubmit={onSubmit} />
      // word vault
      case 'word-vault':
        return <WordVault onSubmit={onSubmit} />
      // whitelist management
      case 'whitelist':
        return <Whitelist onSubmit={onSubmit} />
      // application exclusion
      case 'app-exclusion':
        return <AppExclusionList />
      // application exclusion
      case 'acceptable-use-policies':
        return <AUPlist onSubmit={onSubmit} />
      // alert management
      case 'manage-alerts':
        return <UserMgmtAlerts onSubmit={onSubmit} setSelectedForm={setSelectedForm} />
      case 'weblogs':
        return <WeblogList />
      case 'integrations-cisco':
        return <CiscoSplash />
      case 'cisco-block-requests':
        return <CiscoRequests />
      case 'cisco-destination-lists':
        return <CiscoLists />
      default:
        return <></>
    }
  }

  // TODO display success or error
  const onSubmit = async (prop, form) => {
    const settings = await dispatch(updateUserGeneralSettings(prop, form))
      .catch((err) => {
        log.error('Error updating settings')
        log.error(err)
      })
    return settings
  }

  const toggleCollapseList = (list) => {
    setNavPanel(list)
  }

  const toggleSelectedForm = (list) => {
    setSelectedForm(list)
  }

  const renderSettingsContent = () => {
    return (
      <>
        {/* user profile */}
        <Accordion expanded={navPanel === 'users'} onChange={() => { toggleCollapseList('users'); toggleSelectedForm('user-general') }}>
          <AccordionSummary expandIcon={<ExpandIcon />} className={classes.accordionHeader}>
            <Typography variant='body1'><PersonIcon fontSize='default' className='mr-2' /> My Profile</Typography>
          </AccordionSummary>

          <AccordionDetails className={classes.accordionContent}>
            <List className='w-100' disablePadding>
              <ListItem button onClick={() => { if (selectedForm !== 'user-general') { toggleSelectedForm('user-general') } }} className={selectedForm === 'user-general' ? classes.selectedListItem : classes.listItem}>
                <ListItemText primary='My Settings' primaryTypographyProps={{ color: 'inherit', variant: 'button' }} />
              </ListItem>
            </List>
          </AccordionDetails>
          {/* <ListGroupItem
                onClick={(e) => { e.preventDefault(); toggleSelectedForm('user-my-reports') }}
                className={`${selectedForm === 'user-my-reports' ? 'active' : ''}`}
              >
                My Reports
              </ListGroupItem> */}
        </Accordion>

        {/* organization profile */}
        {
          canEditAccount
            ? (
              <>
                <Accordion expanded={navPanel === 'org'} onChange={() => { toggleCollapseList('org'); toggleSelectedForm('organization-general') }}>
                  <AccordionSummary expandIcon={<ExpandIcon />} className={classes.accordionHeader}>
                    <Typography variant='body1'><DomainIcon fontSize='default' className='mr-2' /> Account</Typography>
                  </AccordionSummary>

                  <AccordionDetails className={classes.accordionContent}>
                    <List className='w-100' disablePadding>
                      <ListItem button onClick={() => toggleSelectedForm('organization-general')} className={selectedForm === 'organization-general' ? classes.selectedListItem : classes.listItem}>
                        <ListItemText primary='Account Info' primaryTypographyProps={{ color: 'inherit', variant: 'button' }} />
                      </ListItem>

                      <ListItem button onClick={() => toggleSelectedForm('organization-contacts')} className={selectedForm === 'organization-contacts' ? classes.selectedListItem : classes.listItem}>
                        <ListItemText primary='Account Contacts' primaryTypographyProps={{ color: 'inherit', variant: 'button' }} />
                      </ListItem>

                      <ListItem button onClick={() => toggleSelectedForm('organization-license')} className={selectedForm === 'organization-license' ? classes.selectedListItem : classes.listItem}>
                        <ListItemText primary='Account Licenses' primaryTypographyProps={{ color: 'inherit', variant: 'button' }} />
                      </ListItem>

                      <ListItem button onClick={() => toggleSelectedForm('organization-settings')} className={selectedForm === 'organization-settings' ? classes.selectedListItem : classes.listItem}>
                        <ListItemText primary='Advanced Settings' primaryTypographyProps={{ color: 'inherit', variant: 'button' }} />
                      </ListItem>

                      {/*
                        <ListItem button onClick={() => toggleSelectedForm('organization-email') }>
                          Email Templates
                        </ListItem>
                      */}
                    </List>
                  </AccordionDetails>
                </Accordion>
              </>)
            : (<></>)
        }

        {/* user management */}

        {
          canEditSupervisors
            ? (
              <>
                <Accordion expanded={navPanel === 'user-mgmt'} onChange={() => { toggleCollapseList('user-mgmt'); toggleSelectedForm('user-mgmt-list-users') }}>
                  <AccordionSummary expandIcon={<ExpandIcon />} className={classes.accordionHeader}>
                    <Typography variant='body1'><SupervisedUserCircleIcon fontSize='default' className='mr-2' /> Supervisors</Typography>
                  </AccordionSummary>

                  <AccordionDetails className={classes.accordionContent}>
                    <List className='w-100' disablePadding>
                      <ListItem button onClick={() => toggleSelectedForm('user-mgmt-list-users')} className={selectedForm === 'user-mgmt-list-users' ? classes.selectedListItem : classes.listItem}>
                        <ListItemText primary='Supervisor Manager' primaryTypographyProps={{ color: 'inherit', variant: 'button' }} />
                      </ListItem>
                      <ListItem button onClick={() => toggleSelectedForm('supervisor-groups')} className={selectedForm === 'supervisor-groups' ? classes.selectedListItem : classes.listItem}>
                        <ListItemText primary='Supervisor Groups' primaryTypographyProps={{ color: 'inherit', variant: 'button' }} />
                      </ListItem>
                    </List>
                  </AccordionDetails>
                </Accordion>
              </>)
            : (<></>)
        }

        {/* user roles */}

        {
          canEditRoles
            ? (
              <>
                <Accordion expanded={navPanel === 'user-roles'} onChange={() => { toggleCollapseList('user-roles'); toggleSelectedForm('user-roles-list') }}>
                  <AccordionSummary expandIcon={<ExpandIcon />} className={classes.accordionHeader}>
                    <Typography variant='body1'><StyleIcon fontSize='default' className='mr-2' /> Roles</Typography>
                  </AccordionSummary>

                  <AccordionDetails className={classes.accordionContent}>
                    <List className='w-100' disablePadding>
                      <ListItem button className={selectedForm === 'user-roles-list' ? classes.selectedListItem : classes.listItem}>
                        <ListItemText primary='Role Manager' primaryTypographyProps={{ color: 'inherit', variant: 'button' }} />
                      </ListItem>
                    </List>
                  </AccordionDetails>
                </Accordion>
              </>)
            : (<></>)
        }

        {/* organizational management */}

        {
          canEditUnits
            ? (
              <>
                <Accordion expanded={navPanel === 'org-mgmt'} onChange={() => { toggleCollapseList('org-mgmt'); toggleSelectedForm('org-mgmt') }}>
                  <AccordionSummary expandIcon={<ExpandIcon />} className={classes.accordionHeader}>
                    <Typography variant='body1'><AccountTreeIcon fontSize='default' className='mr-2' /> Organizational Units</Typography>
                  </AccordionSummary>

                  <AccordionDetails className={classes.accordionContent}>
                    <List className='w-100' disablePadding>
                      <ListItem button className={selectedForm === 'org-mgmt' ? classes.selectedListItem : classes.listItem}>
                        <ListItemText primary='OU Settings' primaryTypographyProps={{ color: 'inherit', variant: 'button' }} />
                      </ListItem>
                    </List>
                  </AccordionDetails>
                </Accordion>
              </>)
            : (<></>)
        }

        {/* word vault */}

        {
          canEditWords
            ? (
              <>
                <Accordion expanded={navPanel === 'word-vault'} onChange={() => { toggleCollapseList('word-vault'); toggleSelectedForm('word-vault') }}>
                  <AccordionSummary expandIcon={<ExpandIcon />} className={classes.accordionHeader}>
                    <Typography variant='body1'><MenuBookIcon fontSize='default' className='mr-2' /> Word Vault</Typography>
                  </AccordionSummary>

                  <AccordionDetails className={classes.accordionContent}>
                    <List className='w-100' disablePadding>
                      <ListItem button className={selectedForm === 'word-vault' ? classes.selectedListItem : classes.listItem}>
                        <ListItemText primary='Word Vault Manager' primaryTypographyProps={{ color: 'inherit', variant: 'button' }} />
                      </ListItem>
                    </List>
                  </AccordionDetails>
                </Accordion>
              </>)
            : (<></>)
        }
        {/* acceptable use policies */}

        {
          canEditAUPs
            ? (
              <>
                <Accordion expanded={navPanel === 'acceptable-use-policies'} onChange={() => { toggleCollapseList('acceptable-use-policies'); toggleSelectedForm('acceptable-use-policies') }}>
                  <AccordionSummary expandIcon={<ExpandIcon />} className={classes.accordionHeader}>
                    <Typography variant='body1'><GavelIcon fontSize='default' className='mr-2' /> Acceptable Use Policies</Typography>
                  </AccordionSummary>

                  <AccordionDetails className={classes.accordionContent}>
                    <List className='w-100' disablePadding>
                      <ListItem button className={selectedForm === 'acceptable-use-policies' ? classes.selectedListItem : classes.listItem}>
                        <ListItemText primary='Policy Manager' primaryTypographyProps={{ color: 'inherit', variant: 'button' }} />
                      </ListItem>
                    </List>
                  </AccordionDetails>
                </Accordion>
              </>)
            : (<></>)
        }

        {/* whitelist management */}

        {
          canEditWhitelists
            ? (
              <>
                <Accordion expanded={navPanel === 'whitelist'} onChange={() => { toggleCollapseList('whitelist'); toggleSelectedForm('whitelist') }}>
                  <AccordionSummary expandIcon={<ExpandIcon />} className={classes.accordionHeader}>
                    <Typography variant='body1'><LanguageIcon fontSize='default' className='mr-2' /> Whitelist</Typography>
                  </AccordionSummary>

                  <AccordionDetails className={classes.accordionContent}>
                    <List className='w-100' disablePadding>
                      <ListItem button className={selectedForm === 'whitelist' ? classes.selectedListItem : classes.listItem}>
                        <ListItemText primary='Whitelist Manager' primaryTypographyProps={{ color: 'inherit', variant: 'button' }} />
                      </ListItem>
                    </List>
                  </AccordionDetails>
                </Accordion>
              </>)
            : (<></>)
        }

        {/* email / sms alerts */}

        {
          canEditAlerts
            ? (
              <>
                <Accordion expanded={navPanel === 'manage-alerts'} onChange={() => { toggleCollapseList('manage-alerts'); toggleSelectedForm('manage-alerts') }}>
                  <AccordionSummary expandIcon={<ExpandIcon />} className={classes.accordionHeader}>
                    <Typography variant='body1'><NotificationsActiveIcon fontSize='default' className='mr-2' /> Alerts</Typography>
                  </AccordionSummary>

                  <AccordionDetails className={classes.accordionContent}>
                    <List className='w-100' disablePadding>
                      <ListItem button className={selectedForm === 'manage-alerts' ? classes.selectedListItem : classes.listItem}>
                        <ListItemText primary='Alert Manager' primaryTypographyProps={{ color: 'inherit', variant: 'button' }} />
                      </ListItem>
                    </List>
                  </AccordionDetails>
                </Accordion>
              </>)
            : (<></>)
        }

        {/* application exclusions */}

        {
          canEditAppExclusions
            ? (
              <>
                <Accordion expanded={navPanel === 'app-exclusion'} onChange={() => { toggleCollapseList('app-exclusion'); toggleSelectedForm('app-exclusion') }}>
                  <AccordionSummary expandIcon={<ExpandIcon />} className={classes.accordionHeader}>
                    <Typography variant='body1'><SettingsApplicationsIcon fontSize='default' className='mr-2' /> App Exclusions</Typography>
                  </AccordionSummary>

                  <AccordionDetails className={classes.accordionContent}>
                    <List className='w-100' disablePadding>
                      <ListItem button className={selectedForm === 'app-exclusion' ? classes.selectedListItem : classes.listItem}>
                        <ListItemText primary='App Exclusions' primaryTypographyProps={{ color: 'inherit', variant: 'button' }} />
                      </ListItem>
                    </List>
                  </AccordionDetails>
                </Accordion>
              </>)
            : (<></>)
        }

        {
          canViewWeblogs
            ? (
              <>
                <Accordion expanded={navPanel === 'weblogs'} onChange={() => { toggleCollapseList('weblogs'); toggleSelectedForm('weblogs') }}>
                  <AccordionSummary expandIcon={<ExpandIcon />} className={classes.accordionHeader}>
                    <Typography variant='body1'><WebIcon fontSize='default' className='mr-2' /> Weblogs</Typography>
                  </AccordionSummary>

                  <AccordionDetails className={classes.accordionContent}>
                    <List className='w-100' disablePadding>
                      <ListItem button className={selectedForm === 'weblogs' ? classes.selectedListItem : classes.listItem}>
                        <ListItemText primary='Weblogs' primaryTypographyProps={{ color: 'inherit', variant: 'button' }} />
                      </ListItem>
                    </List>
                  </AccordionDetails>
                </Accordion>
              </>)
            : (<></>)
        }

        {
          canEditIntegrations
            ? (
              <>
                <Accordion expanded={navPanel === 'integrations'} onChange={() => { toggleCollapseList('integrations'); toggleSelectedForm('integrations-cisco') }}>
                  <AccordionSummary expandIcon={<ExpandIcon />} className={classes.accordionHeader}>
                    <Typography variant='body1'><IntegrationsIcon fontSize='default' className='mr-2' /> Integrations</Typography>
                  </AccordionSummary>

                  <AccordionDetails className={classes.accordionContent}>
                    <List className='w-100' disablePadding>
                      <ListItem button onClick={() => toggleSelectedForm('integrations-cisco')} className={selectedForm === 'integrations-cisco' ? classes.selectedListItem : classes.listItem}>
                        <ListItemText primary='Integrations' primaryTypographyProps={{ color: 'inherit', variant: 'button' }} />
                      </ListItem>

                      {/* {
                        canApproveBloc
                          ? (
                            <ListItem button onClick={() => toggleSelectedForm('cisco-approve')} className={selectedForm === 'cisco-approve' ? classes.selectedListItem : classes.listItem}>
                              <ListItemText primary='Cisco Umbrella' primaryTypographyProps={{ color: 'inherit', variant: 'button' }} />
                            </ListItem>
                          ) : (<></>)
                      } */}
                    </List>
                  </AccordionDetails>
                </Accordion>
              </>)
            : (<></>)
        }

        {
          canApproveBloc
            ? (
              <>
                <Accordion expanded={navPanel === 'cisco'} onChange={() => { toggleCollapseList('cisco'); toggleSelectedForm('cisco-destination-lists') }}>
                  <AccordionSummary expandIcon={<ExpandIcon />} className={classes.accordionHeader} disabled={isCiscoDisabled}>
                    <Grid container direction='row' alignItems='center' spacing={1} className='m-auto w-100'>
                      <Grid item className='py-0 pl-0'>
                        <Typography variant='body1'><CiscoIcon fontSize='default' className='mr-2' /> Cisco Umbrella</Typography>
                      </Grid>

                      <Grid item className='pr-0 py-0'>
                        <Typography variant='button' className='font-weight-bold'>BETA</Typography>
                      </Grid>
                    </Grid>
                  </AccordionSummary>

                  <AccordionDetails className={classes.accordionContent}>
                    <List className='w-100' disablePadding>
                      <ListItem button onClick={() => toggleSelectedForm('cisco-destination-lists')} className={selectedForm === 'cisco-destination-lists' ? classes.selectedListItem : classes.listItem}>
                        <ListItemText primary='Cisco Destination Lists' primaryTypographyProps={{ color: 'inherit', variant: 'button' }} />
                      </ListItem>

                      <ListItem button onClick={() => toggleSelectedForm('cisco-block-requests')} className={selectedForm === 'cisco-block-requests' ? classes.selectedListItem : classes.listItem}>
                        <ListItemText primary='Cisco Block Requests' primaryTypographyProps={{ color: 'inherit', variant: 'button' }} />
                      </ListItem>
                    </List>
                  </AccordionDetails>
                </Accordion>
              </>)
            : (<></>)
        }

        {/* <Accordion>
          <AccordionSummary expandIcon={<ExpandIcon />} className={classes.accordionHeader} disabled>
            <Typography variant='body1'><HelpOutlineIcon fontSize='default' className='mr-2' /> Help & Support</Typography>
          </AccordionSummary>
        </Accordion> */}
      </>
    )
  }

  const handleToggle = async () => {
    setSelectedForm('user-general')
    setNavPanel('users')
    await toggleModal()
  }

  return (
    <>
      <Dialog open={open !== undefined ? open : false} fullWidth maxWidth='lg' scroll='body' disableBackdropClick disableEscapeKeyDown className={classes.settingsDialog}>
        <DialogTitle disableTypography>
          <Grid container direction='row' className='mx-auto w-100' justify='space-between'>
            <Grid item>
              <Typography variant='h4' className={`font-weight-bold ${classes.settingsTitle}`}>Admin Settings</Typography>
            </Grid>
            <Grid item>
              <IconButton className={classes.closeButton} onClick={handleToggle}>
                <CloseIcon />
              </IconButton>
            </Grid>
          </Grid>
        </DialogTitle>

        <DialogContent dividers className={classes.settingsContent}>
          <Grid container direction='row' spacing={2} className='w-100 mx-auto' justify='space-between'>
            <Grid container item direction='row' xs={3}>
              <Paper className={classes.settingsPaper} elevation={4}>
                {/* Nav */}
                {renderSettingsContent()}
              </Paper>
            </Grid>

            <Grid container item direction='row' xs={9}>
              <Paper className={classes.formPaper} elevation={4}>
                {/* Form */}
                {getSelectedForm()}
              </Paper>
            </Grid>

            {/*
              TO DO: change keyword upon opening 'Settings' modal
            */}
            {/* <ElevioIntegration keywords={['Settings']} /> */}
          </Grid>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default SettingsScreen
